import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";
import { IESDoc } from "../../../app.interface";
import { ModifierGroup } from "../../pos/types/pos.types";

export interface MenuItem {
  menu_item_id: string;
  name: string;
  description?: string;
  category?: string;
  price: number;
  total_cost: number;
  sellable_items: Array<{
    _id: string;
    name: string;
    quantity: string;
  }>;
  allergies: string[];
  expected_prep_duration?: string;
  ingredients: Array<{
    _id: string;
    name: string;
    quantity: string;
  }>;
  branch_id?: string;
  is_available: boolean;
  is_active: boolean;
  is_deleted: boolean;
  store_id: string;
  created_at: number;
  created_by_id: string;
  created_by_email: string;
  modifiers?: ModifierGroup[];
}

interface ListMenuItemsRequest {
  searchQuery?: string;
  filters?: Array<{
    id: string;
    title: string;
    field: string;
    operator: string;
    value: any;
  }>;
  startDate?: number;
  endDate?: number;
  dateField?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageIndex?: number;
  pageSize?: number;
  category?: string;
}

interface CreateMenuItemRequest {
  name: string;
  description?: string;
  category?: string;
  price: number;
  total_cost: number;
  sellable_items: Array<{
    _id: string;
    name: string;
    quantity: string;
  }>;
  allergies?: string[];
  expected_prep_duration?: string;
  ingredients?: Array<{
    _id: string;
    name: string;
    quantity: string;
  }>;
  branch_id?: string;
  is_available?: boolean;
  modifiers?: ModifierGroup[];
}

interface UpdateMenuItemRequest {
  _id: string;
  name?: string;
  description?: string;
  category?: string;
  price?: number;
  total_cost?: number;
  sellable_items?: Array<{
    _id: string;
    name: string;
    quantity: string;
  }>;
  allergies?: string[];
  expected_prep_duration?: string;
  ingredients?: Array<{
    _id: string;
    name: string;
    quantity: string;
  }>;
  branch_id?: string;
  is_available?: boolean;
  modifiers: ModifierGroup[];
}

interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

interface IESParsedResponse {
  data: any[];
  metadata: {
    total: any;
    took: any;
    timed_out: any;
    max_score: any;
  };
  aggs: any;
  suggest: any;
}

class MenuItemsService {
  private readonly baseUrl = "/business-admin/menu-items";
  async listMenuItems(
    params: ListMenuItemsRequest
  ): Promise<IESParsedResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/list`, params);
    return response.data.data;
  }

  async getMenuItem(id: string): Promise<APIResponse<MenuItem>> {
    const response = await axiosInstance.post(`${this.baseUrl}/get`, {
      _id: id,
    });
    return response.data;
  }

  async createMenuItem(
    data: CreateMenuItemRequest
  ): Promise<APIResponse<MenuItem>> {
    const response = await axiosInstance.post(`${this.baseUrl}/create`, data);
    return response.data;
  }

  async updateMenuItem(
    data: UpdateMenuItemRequest
  ): Promise<APIResponse<MenuItem>> {
    const response = await axiosInstance.post(`${this.baseUrl}/update`, data);
    return response.data;
  }

  async deleteMenuItem(id: string): Promise<APIResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/delete`, {
      _id: id,
    });
    return response.data;
  }

  async toggleMenuItemActive(id: string): Promise<APIResponse<MenuItem>> {
    const response = await axiosInstance.post(`${this.baseUrl}/toggle-active`, {
      _id: id,
    });
    return response.data;
  }

  async toggleMenuItemAvailability(id: string): Promise<APIResponse<MenuItem>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/toggle-availability`,
      { _id: id }
    );
    return response.data;
  }
}

export const menuItemsService = new MenuItemsService();
